import { apiPaths, deleteData } from '../platform-components/api-integration-util';
import { getUserDetails } from '../platform-components/utils/shared-data.service';
import { checkPermission } from '../platform-components/utils/configuration';

/**
 * One place for "delete this question from the question bank", shared by every surface that
 * offers it: the Question Repo table, the question popup, and the Dashboard's bulk delete.
 *
 * It exists because the delete is not a single call - it is a call plus a permission decision
 * plus a two-step confirm/override flow, and three copies of that drifted apart the moment one
 * of them gained the override.
 *
 * Permission model (mirrored server-side by requireDeletePermission in QuestionBankApp's
 * QuestionBankService - keep the two in sync):
 *
 *   Question Repo Actions / Delete Question        - may delete an unmapped question
 *   Question Repo Actions / Force Delete Question  - may also use "Delete anyway", which unmaps
 *                                                    the question from every module/test first
 *
 * Roles that could delete before this category existed keep "Delete Question" through
 * migratePermissions(); "Force Delete Question" is never inherited.
 */

export const QUESTION_ACTIONS_CATEGORY = 'Question Repo Actions';
export const DELETE_QUESTION_PERMISSION = 'Delete Question';
export const FORCE_DELETE_QUESTION_PERMISSION = 'Force Delete Question';

export const canDeleteQuestion = () =>
  checkPermission(QUESTION_ACTIONS_CATEGORY, DELETE_QUESTION_PERMISSION);

export const canForceDeleteQuestion = () =>
  checkPermission(QUESTION_ACTIONS_CATEGORY, FORCE_DELETE_QUESTION_PERMISSION);

/**
 * DELETE questions/delete.
 *
 * @param {number[]} questionIds ids to delete - the backend refuses the whole batch or none of it
 * @param {boolean}  force       send the "delete anyway" override
 * @returns {Promise<{ ok: boolean, message?: string, blocked?: boolean }>}
 *
 * Never rejects. deleteData() rejects with the API's `msg` string on any non-2xx, and the two
 * failures that matter here are both expected states rather than faults:
 *   409 - the question is still mapped (offer the override), or has already been answered
 *   403 - the role does not grant this delete
 * Returning them as a value keeps every caller from having to remember the try/catch that the
 * Question Repo screen originally forgot, where the click looked like it did nothing at all.
 */
export const deleteQuestions = async (questionIds, { force = false } = {}) => {
  try {
    const response = await deleteData(apiPaths.deleteQuestion, {
      data: questionIds,
      params: force ? { force: true } : undefined,
      headers: {
        tenantId: getUserDetails().tenantId,
        userEmail: getUserDetails().email,
      },
    });
    if (response?.result === 'Success') {
      return { ok: true };
    }
    return { ok: false, message: 'Error in deleting question' };
  } catch (err) {
    return {
      ok: false,
      blocked: typeof err === 'string',
      message: typeof err === 'string' ? err : 'Error in deleting question',
    };
  }
};
